/**
 * Editor Image Upload Service
 *
 * Handles the full lifecycle of inline images in the Slate editor:
 *
 * ON PASTE  → Image stays as base64 in editor state (handled by withImagePaste plugin)
 * ON SAVE   → base64 images uploaded to S3 via Mnemosyne, replaced with docId
 * ON LOAD   → docIds resolved to presigned S3 URLs (bulk API, cached)
 * ON DELETE → removed images soft-deleted from Mnemosyne
 * ON PDF    → docIds resolved to base64 for @react-pdf/renderer
 */

import {
    getSignedUrl,
    uploadFile,
    saveDocMetaDataInDocService,
    getPresignedUrl,
    getPresignedUrlBulk,
    deleteEditorImages,
} from '../../../modules/Company/UploadJourney/components/UploadCard/utils/api/helpers';
import { Descendant } from 'slate';
import { PROMISE_STATES } from '../../../utils/axios-interceptor';

interface ImageInfo {
    path: number[];
    url: any;
    type: 'base64' | 'docId';
}

const EDITOR_IMAGE_CATEGORY_ID = 100649;
const EDITOR_IMAGE_FLOW_ID = 16;
const CACHE_TTL_MS = 10 * 60 * 1000; // 10 min cache for presigned URLs

// After save, DB may return url as number (12345) or string ("12345")
const isBase64 = (url: any): boolean => typeof url === 'string' && url.startsWith('data:image/');

export const isDocId = (url: any): boolean => {
    if (typeof url === 'number') return true;
    if (typeof url === 'string') return /^\d+$/.test(url) && !isBase64(url);
    return false;
};

const str = (v: any): string => String(v);

// Single-pass tree walk — collects both base64 and docId image nodes with their paths
function collectImages(nodes: any[], parentPath: number[] = []): ImageInfo[] {
    if (!Array.isArray(nodes)) return [];
    const images: ImageInfo[] = [];
    nodes.forEach((node: any, i: number) => {
        const path = [...parentPath, i];
        if (node.type === 'image' && node.url) {
            if (isBase64(node.url)) images.push({ path, url: node.url, type: 'base64' });
            else if (isDocId(node.url)) images.push({ path, url: node.url, type: 'docId' });
        }
        if (node.children) images.push(...collectImages(node.children, path));
    });
    return images;
}

// Set url on an image node at a given path (used after upload to replace base64 with docId)
function setNodeUrl(root: any[], path: number[], url: string) {
    let node: any = root;
    for (let i = 0; i < path.length - 1; i++) {
        node = node[path[i]]?.children ?? node[path[i]];
    }
    const target = node[path[path.length - 1]];
    if (target?.type === 'image') target.url = url;
}

// Deep clone nodes and replace all docId image urls using a lookup map
export function replaceImageUrls(nodes: any[], urlMap: Record<string, string>): any[] {
    const cloned = JSON.parse(JSON.stringify(nodes));
    const walk = (list: any[]) => {
        for (const node of list) {
            if (node.type === 'image' && isDocId(node.url) && urlMap[str(node.url)]) {
                node.url = urlMap[str(node.url)];
            }
            if (node.children) walk(node.children);
        }
    };
    walk(cloned);
    return cloned;
}

// Convert base64 data URL to File object for S3 upload
function base64ToFile(dataUrl: string, fileName: string): File {
    const [header, data] = dataUrl.split(',');
    const mime = header.match(/:(.*?);/)?.[1] ?? 'image/png';
    const bytes = Uint8Array.from(atob(data), (c) => c.charCodeAt(0));
    return new File([bytes], `${fileName}.${mime.split('/')[1] || 'png'}`, { type: mime });
}

// Fetch image from URL and convert to base64 data URL (for PDF rendering)
async function urlToBase64(url: string): Promise<string> {
    const blob = await (await fetch(url)).blob();
    return new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onloadend = () => resolve(r.result as string);
        r.onerror = reject;
        r.readAsDataURL(blob);
    });
}

// Upload a single image: getSignedUrl → PUT to S3 → saveMetadata → returns docId
export async function uploadSingleImage(base64Url: string, source: string): Promise<string> {
    const orgId = localStorage.getItem('user_org_id') || '';
    const file = base64ToFile(base64Url, `${source}-${Date.now()}`);

    const signRes = await getSignedUrl(
        { file },
        {
            assesseeOrgId: orgId,
            categoryId: EDITOR_IMAGE_CATEGORY_ID,
            mnemosyneOrgId: orgId,
        },
    );
    if (!signRes?.signedUrl) throw new Error('Failed to get signed URL');

    await uploadFile(file, signRes.signedUrl);

    const doc = await saveDocMetaDataInDocService({
        input: {
            categoryId: EDITOR_IMAGE_CATEGORY_ID,
            mnemosyneOrgId: orgId,
            fileName: file.name,
            fileSizeKb: file.size / 1000,
            flowId: EDITOR_IMAGE_FLOW_ID,
            fileType: file.type,
        },
        orgId,
    });
    if (!doc?.id) throw new Error('Failed to get docId');
    return String(doc.id);
}

// Shared by single fetch, bulk fetch, and PDF resolve
const cache: Record<string, { url: string; exp: number }> = {};
let bulkPromise: Promise<Record<string, string>> | null = null;

// Track lazy uploads triggered by the Plate editor (D2). processImagesBeforeSave
// awaits these so a save fired before the upload completes won't ship base64.
const pendingUploads: Map<string, Promise<unknown>> = new Map();

export function registerPendingUpload(placeholderId: string, promise: Promise<unknown>) {
    pendingUploads.set(placeholderId, promise);
    promise.finally(() => {
        if (pendingUploads.get(placeholderId) === promise) pendingUploads.delete(placeholderId);
    });
}

export async function awaitAllPendingUploads(): Promise<void> {
    if (pendingUploads.size === 0) return;
    await Promise.allSettled(Array.from(pendingUploads.values()));
}

function cacheGet(id: string): string | null {
    const entry = cache[id];
    return entry && entry.exp > Date.now() ? entry.url : null;
}

function cacheSet(id: string, url: string) {
    cache[id] = { url, exp: Date.now() + CACHE_TTL_MS };
}

// Extract all docIds from Slate content (for bulk prefetch on editor load)
export function collectDocIdsFromSlate(value: Descendant[]): string[] {
    if (!value?.length) return [];
    return collectImages(value)
        .filter((i) => i.type === 'docId')
        .map((i) => str(i.url));
}

/**
 * Process editor content before saving to DB:
 * 1. Upload new base64 images to S3 (parallel), replace with docId
 * 2. Delete removed images from Mnemosyne (diff current vs original)
 *
 * @param value         - Current Slate editor content
 * @param originalDocIds - DocIds from when the content was loaded ([] for new content)
 * @param source        - Module name for S3 file naming (e.g. 'one-pager', 'comment')
 */
export async function processImagesBeforeSave(
    value: Descendant[],
    originalDocIds: string[] = [],
    source = 'editor',
): Promise<Descendant[]> {
    if (!value?.length) return value;

    // Wait for any lazy uploads kicked off by the Plate editor (D2). After
    // this resolves the base64 entries in `value` may already have been
    // swapped for docIds by setNodes, so we re-walk after the await.
    await awaitAllPendingUploads();

    const images = collectImages(value);
    const base64Images = images.filter((i) => i.type === 'base64');

    let cloned = value;

    // Upload new images
    if (base64Images.length > 0) {
        cloned = JSON.parse(JSON.stringify(value));
        const results = await Promise.allSettled(
            base64Images.map(async (img) => ({
                path: img.path,
                docId: await uploadSingleImage(img.url, source),
            })),
        );
        results.forEach((r) => {
            if (r.status === PROMISE_STATES.FULFILLED) setNodeUrl(cloned as any[], r.value.path, r.value.docId);
        });
    }

    // Delete removed images
    if (originalDocIds.length > 0) {
        const currentIds = new Set(collectDocIdsFromSlate(cloned));
        const removed = originalDocIds.filter((id) => !currentIds.has(id));
        if (removed.length > 0) deleteEditorImages(removed.map(Number));
    }

    return cloned;
}

/**
 * Fetch presigned URL for a single docId (used by Image.jsx).
 * If a bulk fetch is in flight, piggybacks on it instead of making a separate call.
 */
export async function fetchImageUrl(docId: string): Promise<string> {
    if (bulkPromise) {
        const result = await bulkPromise;
        if (result[docId]) return result[docId];
    }

    const cached = cacheGet(docId);
    if (cached) return cached;

    const res = await getPresignedUrl({ docId: Number(docId) }, false);
    if (!res?.uri) throw new Error(`Failed to fetch presigned URL for docId: ${docId}`);
    cacheSet(docId, res.uri);
    return res.uri;
}

/**
 * Bulk fetch presigned URLs (called on editor mount).
 * One API call for N images. Populates cache so Image.jsx gets instant hits.
 */
export async function fetchImageUrlsBulk(docIds: string[]): Promise<Record<string, string>> {
    const result: Record<string, string> = {};
    const uncached: number[] = [];

    for (const id of docIds) {
        const url = cacheGet(id);
        if (url) result[id] = url;
        else uncached.push(Number(id));
    }

    if (uncached.length === 0) return result;

    // Store promise so concurrent fetchImageUrl calls wait for this instead of firing single calls
    bulkPromise = (async () => {
        const bulk = await getPresignedUrlBulk(uncached);
        for (const [id, url] of Object.entries(bulk)) {
            cacheSet(String(id), url);
            result[String(id)] = url;
        }
        return result;
    })();

    try {
        return await bulkPromise;
    } finally {
        bulkPromise = null;
    }
}

/**
 * Resolve docId images to base64 for PDF generation.
 * @react-pdf/renderer can't fetch from S3 presigned URLs (CORS),
 * so we fetch the images ourselves and inline them as base64 data URLs.
 */
export async function resolveDocIdsForPdf(value: Descendant[]): Promise<Descendant[]> {
    if (!value?.length) return value;

    const docIds = collectDocIdsFromSlate(value);
    if (docIds.length === 0) return value;

    // Get fresh presigned URLs (skip cache — cached URLs may have expired for PDF use)
    const freshUrls: Record<string, string> = {};
    const bulk = await getPresignedUrlBulk(docIds.map(Number));
    for (const [id, url] of Object.entries(bulk)) {
        freshUrls[String(id)] = url;
    }

    // Fetch actual image bytes and convert to base64 (parallel)
    const base64Map: Record<string, string> = {};
    await Promise.all(
        docIds.map(async (id) => {
            const presignedUrl = freshUrls[id];
            if (!presignedUrl) {
                console.warn(`[resolveDocIdsForPdf] No presigned URL for docId: ${id}`);
                return;
            }
            try {
                base64Map[id] = await urlToBase64(presignedUrl);
            } catch (e) {
                console.error(`[resolveDocIdsForPdf] Failed to fetch image for docId: ${id}`, e);
            }
        }),
    );

    if (Object.keys(base64Map).length === 0 && docIds.length > 0) {
        console.warn('[resolveDocIdsForPdf] No images resolved. docIds:', docIds, 'freshUrls:', freshUrls);
    }

    return replaceImageUrls(value, base64Map) as Descendant[];
}
